/**
 * Authentication Context
 */

import { createContext, useContext, useState, useEffect, useCallback } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';

const AuthContext = createContext(null);

const COOKIE_MAX_AGE = 60 * 24 * 60 * 60; // 60 days in seconds (fallback; server sets authoritative cookie)

/**
 * Set the rialto_token cookie
 */
function setTokenCookie(token) {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `rialto_token=${token}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

/**
 * Clear the rialto_token cookie
 */
function clearTokenCookie() {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `rialto_token=; path=/; max-age=0; SameSite=Lax${secure}`;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check for existing auth on mount
    useEffect(() => {
        const savedToken = localStorage.getItem('rialto_token');
        const savedUser = localStorage.getItem('rialto_user');

        if (savedToken && savedUser) {
            try {
                const parsed = JSON.parse(savedUser);
                setUser(parsed);

                // Ensure cookie is in sync with localStorage
                // (cookie may have expired while localStorage persists)
                setTokenCookie(savedToken);
            } catch (e) {
                // Corrupted localStorage data — clear it
                localStorage.removeItem('rialto_token');
                localStorage.removeItem('rialto_user');
                clearTokenCookie();
            }
        }

        setLoading(false);
    }, []);

    // Silently clear auth state (for 401 handling — no server call needed)
    const clearAuth = useCallback(() => {
        setUser(null);
        localStorage.removeItem('rialto_token');
        localStorage.removeItem('rialto_user');
        clearTokenCookie();
    }, []);

    // Request verification code
    const requestCode = async (phoneNumber, fullName = '', email = '') => {
        setError(null);
        try {
            const data = { phone_number: phoneNumber };
            if (fullName) data.full_name = fullName;
            if (email) data.email = email;

            const response = await apiFetch({
                path: 'rialto/v1/auth/request-code',
                method: 'POST',
                data,
            });
            return response;
        } catch (err) {
            setError(err.message || 'Failed to send code');
            throw err;
        }
    };

    // Verify code and login
    const verifyCode = async (phoneNumber, code, rememberMe = true) => {
        setError(null);
        try {
            const response = await apiFetch({
                path: 'rialto/v1/auth/verify-code',
                method: 'POST',
                data: {
                    phone_number: phoneNumber,
                    code: code,
                    remember_me: rememberMe,
                },
            });

            if (response.success) {
                const userData = {
                    id: response.user_id,
                    phone: phoneNumber,
                    token: response.token,
                };

                setUser(userData);
                localStorage.setItem('rialto_token', response.token);
                localStorage.setItem('rialto_user', JSON.stringify(userData));

                // Set cookie for server-side auth check + API fallback
                if (response.token) {
                    setTokenCookie(response.token);
                }
            }

            return response;
        } catch (err) {
            setError(err.message || 'Invalid code');
            throw err;
        }
    };

    // Logout (with server call)
    const logout = async () => {
        try {
            if (user?.token) {
                await apiFetch({
                    path: 'rialto/v1/auth/logout',
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${user.token}`,
                    },
                });
            }
        } catch (err) {
            // Ignore logout errors
        }

        clearAuth();
    };

    // Get auth header
    const getAuthHeader = () => {
        if (user?.token) {
            return { 'Authorization': `Bearer ${user.token}` };
        }
        return {};
    };

    const value = {
        user,
        loading,
        error,
        isAuthenticated: !!user,
        requestCode,
        verifyCode,
        logout,
        clearAuth,
        getAuthHeader,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
